/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { validateEmail } from "../../helpers/validateEmail";
import authService from "../../services/authService";
import AuthField from "./AuthField";
import OtpInput from "./OtpInput";

export default function CustomerAuthForm({ recovery = false }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState(location.state?.email || "");
    const [step, setStep] = useState("email");
    const [challenge, setChallenge] = useState(null);
    const [grant, setGrant] = useState(null);
    const [code, setCode] = useState("");
    const [password, setPassword] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [missingAccount, setMissingAccount] = useState(false);
    const [passwordAvailable, setPasswordAvailable] = useState(false);
    const [resendAt, setResendAt] = useState(0);
    const [now, setNow] = useState(Date.now());
    const [expired, setExpired] = useState(false);
    const field = useRef(null);
    const pending = useRef(null);
    const purpose = recovery ? "recovery" : "login";
    const remaining = Math.max(0, Math.ceil((resendAt - now) / 1000));

    useEffect(() => { field.current?.focus(); }, [step]);
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);
    useEffect(() => () => pending.current?.abort(), []);

    function showError(message) {
        setFeedback({ error: true, message });
        field.current?.focus();
    }
    function finish(response) {
        login(response.token, response.user);
        setPassword("");
        setConfirmation("");
        setCode("");
        setGrant(null);
        navigate("/tickets", { replace: true });
    }
    async function run(operation) {
        if (pending.current) return;
        const controller = new AbortController();
        pending.current = controller;
        setBusy(true);
        setFeedback(null);
        try {
            await operation(controller.signal);
        } catch (error) {
            if (controller.signal.aborted) return;
            if (error.retryAfter) {
                setResendAt(Date.now() + error.retryAfter * 1000);
                setNow(Date.now());
            }
            if (error.code === "ACCOUNT_NOT_FOUND") setMissingAccount(true);
            if (typeof error.hasPassword === "boolean") setPasswordAvailable(error.hasPassword);
            if (["RECOVERY_EXPIRED", "RECOVERY_INVALID", "ACCOUNT_UNAVAILABLE"].includes(error.code)) {
                setGrant(null);
                setChallenge(null);
                setStep("email");
            }
            if (["OTP_EXPIRED", "OTP_USED", "OTP_ATTEMPTS", "OTP_INVALID"].includes(error.code)) setExpired(true);
            showError(error.message);
        } finally {
            if (!controller.signal.aborted) {
                pending.current = null;
                setBusy(false);
                // Disabled inputs cannot receive focus until the next render.
                requestAnimationFrame(() => field.current?.focus());
            }
        }
    }
    async function sendCode(signal) {
        const normalized = email.trim().toLowerCase();
        const response = await authService.requestCode(normalized, purpose, signal);
        if (signal.aborted) return;
        setEmail(normalized);
        setChallenge(response);
        setPasswordAvailable(response.hasPassword);
        setCode("");
        setExpired(false);
        setMissingAccount(false);
        setGrant(null);
        setResendAt(Date.now() + response.resendAfter * 1000);
        setNow(Date.now());
        setStep("otp");
        setFeedback({ message: "Código enviado. Revisá también la carpeta de spam." });
    }
    function changeEmail() {
        setChallenge(null);
        setGrant(null);
        setCode("");
        setPassword("");
        setConfirmation("");
        setFeedback(null);
        setMissingAccount(false);
        setPasswordAvailable(false);
        setResendAt(0);
        setStep("email");
    }
    function selectStep(next) {
        setFeedback(null);
        setPassword("");
        setConfirmation("");
        setStep(next);
    }
    function openPasswordLogin() {
        if (!validateEmail(email.trim())) return showError("Ingresá un email válido.");
        setEmail(email.trim().toLowerCase());
        selectStep("password");
    }
    function requestCode() {
        if (busy || pending.current || remaining > 0) return;
        if (!validateEmail(email.trim())) return showError("Ingresá un email válido.");
        return run(sendCode);
    }
    async function submit(event) {
        event.preventDefault();
        if (busy || pending.current) return;
        if (step === "email") {
            return recovery ? requestCode() : openPasswordLogin();
        }
        if (step === "password") {
            if (!password) return showError("Ingresá tu contraseña.");
            return run(async (signal) => {
                const response = await authService.login(email.trim().toLowerCase(), password, signal);
                if (!signal.aborted) finish(response);
            });
        }
        if (step === "otp") {
            if (!/^\d{6}$/.test(code)) return showError("Ingresá el código de 6 dígitos.");
            return run(async (signal) => {
                const response = await authService.verifyCode(challenge.challengeId, code, purpose, signal);
                if (signal.aborted) return;
                setCode("");
                if (recovery) {
                    setGrant(response);
                    setStep("choice");
                } else finish(response);
            });
        }
        if (step === "newPassword") {
            if (password.length < 8 || new TextEncoder().encode(password).length > 72) {
                return showError("Usá al menos 8 caracteres y hasta 72 bytes para la contraseña.");
            }
            if (password !== confirmation) {
                setFeedback({ error: true, message: "Las contraseñas no coinciden." });
                document.getElementById("auth-confirmation")?.focus();
                return;
            }
            return complete("change_password");
        }
    }
    function complete(action) {
        return run(async (signal) => {
            const response = await authService.completeRecovery(challenge.challengeId, grant.recoveryToken, action, password, signal);
            if (signal.aborted) return;
            if (response.token) finish(response);
            else if (response.success && response.passwordChanged) {
                setPassword(""); setConfirmation(""); setGrant(null); setChallenge(null); setStep("done");
            } else throw new Error("No pudimos completar la recuperación. Intentá nuevamente.");
        });
    }
    const invalid = Boolean(feedback?.error);
    const primaryLabel = step === "email" ? recovery ? "Enviar código" : "Continuar" : step === "otp" ? "Verificar código e ingresar" :
        step === "newPassword" ? grant?.canLogin === false ? "Guardar nueva contraseña" : "Guardar contraseña e ingresar" : "Ingresar";
    const codeExpired = expired || Boolean(challenge && now >= Date.parse(challenge.expiresAt));

    if (step === "done") return <div>
        <div ref={field} tabIndex={-1} role="status" className="rounded-xl border border-rose-500/20 bg-rose-500/10 p-4">
            <h2 className="text-xl font-semibold">Contraseña actualizada</h2>
            <p className="mt-2 text-sm text-zinc-300">La contraseña se guardó. Para ingresar, tu cuenta de productor debe estar aprobada.</p>
        </div>
        <Link to="/login" className="auth-link mt-5 inline-block text-sm text-rose-300">Volver a iniciar sesión</Link>
    </div>;

    return (
        <div>
            {step !== "email" && <div className="mb-5 flex w-full items-center justify-between gap-3 rounded-xl border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm">
                <p className="min-w-0 flex-1 break-all font-medium text-rose-100">{email}</p>
                <button type="button" disabled={busy} onClick={changeEmail} className="auth-link shrink-0 text-rose-300">Cambiar email</button>
            </div>}
            <form noValidate onSubmit={submit} aria-busy={busy} className="space-y-5">
                {step === "email" && <AuthField ref={field} id="auth-email" label="Email" type="email" autoComplete="email"
                    value={email} disabled={busy} invalid={invalid} placeholder="tu@email.com" onChange={(e) => {
                        setEmail(e.target.value); setMissingAccount(false); setPasswordAvailable(false); setResendAt(0); setFeedback(null);
                    }}
                    help={recovery ? "Te enviaremos un código para recuperar el acceso." : "Continuá con tu contraseña o elegí recibir un código por email."} />}
                {step === "otp" && <OtpInput key={challenge.challengeId} ref={field} id="auth-code" label="Código de 6 dígitos"
                    value={code} disabled={busy} invalid={invalid} onChange={setCode}
                    help={codeExpired ? "El código venció o no está disponible. Pedí uno nuevo." : "Vence en 5 minutos. Si pedís otro, usá el último código recibido."} />}
                {step === "password" && <AuthField key="password" ref={field} id="auth-password" label="Contraseña" sensitive autoComplete="current-password"
                    value={password} disabled={busy} invalid={invalid} onChange={(e) => setPassword(e.target.value)} />}
                {step === "choice" && <div>
                    <h2 ref={field} tabIndex={-1} className="text-xl font-semibold">Email verificado</h2>
                    <p className="mt-2 text-sm text-zinc-300">{grant.canLogin === false ? "Podés cambiar tu contraseña. Para ingresar, tu cuenta de productor debe estar aprobada." :
                        `Podés ${grant.hasPassword ? "cambiar tu contraseña" : "crear una contraseña"} o entrar ahora con el código que verificaste.`}</p>
                    <p className="mt-2 text-xs text-zinc-400">Elegí cómo continuar en los próximos 5 minutos.</p>
                </div>}
                {step === "newPassword" && <>
                    <AuthField ref={field} id="auth-new-password" label="Nueva contraseña" sensitive autoComplete="new-password" value={password}
                        disabled={busy} invalid={invalid} onChange={(e) => setPassword(e.target.value)} help="Usá al menos 8 caracteres. Podés pegar una contraseña de tu gestor." />
                    <AuthField id="auth-confirmation" label="Repetir contraseña" sensitive autoComplete="new-password" value={confirmation}
                        disabled={busy} invalid={invalid} onChange={(e) => setConfirmation(e.target.value)} />
                </>}
                <div hidden={!feedback && !missingAccount} aria-live="polite" aria-atomic="true">
                    {feedback && <p id="auth-feedback" role={feedback.error ? "alert" : "status"}
                        className={`rounded-xl border px-3 py-2 text-sm ${feedback.error ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-zinc-700 bg-zinc-800/50 text-zinc-300"}`}>{feedback.message}</p>}
                    {missingAccount && <Link to="/register" state={{ email: email.trim().toLowerCase() }} className="auth-link mt-3 inline-block text-sm text-rose-300">Registrarme con este email</Link>}
                </div>
                {step === "choice" ? <>
                    <button type="button" disabled={busy} onClick={() => selectStep("newPassword")} className="btn-primary auth-submit w-full px-4 py-3">
                        {grant.hasPassword ? "Crear una nueva contraseña" : "Crear contraseña"}
                    </button>
                    {grant.canLogin !== false && <button type="button" disabled={busy} onClick={() => complete("login")} className="auth-secondary">
                        {busy ? "Ingresando…" : grant.hasPassword ? "Continuar sin cambiarla" : "Ingresar sin crear contraseña"}
                    </button>}
                </> : <button type="submit" disabled={busy || (step === "email" && recovery && remaining > 0) || (step === "otp" && codeExpired)} className="btn-primary auth-submit w-full px-4 py-3 text-sm">
                    {busy && (step !== "email" || recovery) ? "Procesando…" : step === "otp" && recovery ? "Verificar código" : primaryLabel}
                </button>}
            </form>
            <div className="mt-5 flex flex-col gap-3 text-sm">
                {step === "otp" && <>
                    <button type="button" disabled={busy || remaining > 0} onClick={() => run(sendCode)} className="auth-link text-rose-300">
                        {remaining > 0 ? `Reenviar código en ${remaining} s` : "Reenviar código"}
                    </button>
                    {!recovery && passwordAvailable && <button type="button" disabled={busy} onClick={() => selectStep("password")} className="auth-secondary">Ingresar con contraseña</button>}
                    {!recovery && !passwordAvailable && <Link to="/rescue-pass" state={{ email }} className="auth-link text-zinc-300">Crear una contraseña</Link>}
                </>}
                {(step === "email" || (step === "password" && !challenge)) && remaining > 0 && <p className="text-xs text-zinc-400">Podés pedir otro código en {remaining} s.</p>}
                {step === "email" && !missingAccount && !recovery && <button type="button" disabled={busy || remaining > 0} onClick={requestCode} className="auth-secondary">
                    {busy ? "Enviando código…" : "Ingresar sin contraseña"}
                </button>}
                {step === "password" && <>
                    <button type="button" disabled={busy || (!challenge && remaining > 0)} onClick={() => {
                        if (challenge) selectStep("otp");
                        else requestCode();
                    }} className="auth-secondary">Ingresar sin contraseña</button>
                    <Link to="/rescue-pass" state={{ email }} className="auth-link text-rose-300">Olvidé mi contraseña</Link>
                </>}
                {step === "newPassword" && <button type="button" disabled={busy} onClick={() => selectStep("choice")} className="auth-link text-zinc-300">Volver a las opciones de ingreso</button>}
                {step === "email" && !missingAccount && <Link to="/register" state={{ email }} className="auth-link text-rose-300">Crear cuenta</Link>}
                {recovery && <Link to="/login" state={{ email }} className="auth-link text-zinc-300">Volver a iniciar sesión</Link>}
            </div>
        </div>
    );
}
